import { useTranslation } from 'react-i18next';
import { Checkbox } from '@mantine/core';
import { useState } from 'react';

interface BusinessFiltersProps {
    filters: {
        category: string[];
        discount_category: string[];
        publish_prices: boolean[];
        discount: number[];
        country_code: string[];
        city: string[];
        my_companies: boolean[];
    };
    attrFilters: Record<string, string[]>;
    filterOptions: {
        categories: string[];
        discountCategories: string[];
        discounts: number[];
        countryCodes: string[];
        cities: string[];
    };
    attrValues: Record<string, string[]>;
    onFilterChange: (filterType: string, value: any) => void;
    onAttrFilterChange: (attrKey: string, values: string[]) => void;
    onClearAll: () => void;
}

export default function BusinessFilters({
    filters,
    attrFilters,
    filterOptions,
    attrValues,
    onFilterChange,
    onAttrFilterChange,
    onClearAll,
}: BusinessFiltersProps) {
    const { t } = useTranslation();
    const [expandedSections, setExpandedSections] = useState<Set<string>>(
        new Set(['category', 'city', 'country_code', 'discount_category', 'my_companies'])
    );

    const toggleSection = (sectionKey: string) => {
        setExpandedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sectionKey)) {
                newSet.delete(sectionKey);
            } else {
                newSet.add(sectionKey);
            }
            return newSet;
        });
    };

    const toggleFilter = (filterType: string, value: any) => {
        const currentFilter = (filters[filterType as keyof typeof filters] as any[]) || [];
        const isIncluded = currentFilter.includes(value);

        if (isIncluded) {
            onFilterChange(filterType, currentFilter.filter((item: any) => item !== value));
        } else {
            onFilterChange(filterType, [...currentFilter, value]);
        }
    };

    const toggleAttrFilter = (attrKey: string, value: string) => {
        const currentValues = attrFilters[attrKey] || [];
        if (currentValues.includes(value)) {
            onAttrFilterChange(attrKey, currentValues.filter(v => v !== value));
        } else {
            onAttrFilterChange(attrKey, [...currentValues, value]);
        }
    };

    const renderFilterButton = (filterType: string, value: any, isSelected: boolean) => {
        let label = '';
        if (filterType === 'country_code') {
            label = t(`countries.${value}`);
        } else {
            label = t(value);
        }
        return (
            <div
                key={`${filterType}-${value}`}
                className="rounded-md px-2 py-1 mb-0.25 text-sm text-gray-600 font-medium cursor-pointer hover:bg-gray-200"
                onClick={() => toggleFilter(filterType, value)}
            >
                <div className="flex items-center gap-2">
                    <Checkbox
                        color='black'
                        checked={isSelected}
                        onChange={() => { }}
                    />
                    {typeof value === 'boolean' ? t(value ? 'yes' : 'no') : label}
                </div>
            </div>
        );
    };

    const renderAttrFilterButton = (attrKey: string, value: string, isSelected: boolean) => {
        return (
            <div
                key={`${attrKey}-${value}`}
                className="rounded-md px-1 py-0.5 mb-0.25 text-sm text-gray-600 font-medium cursor-pointer hover:bg-gray-200"
                onClick={() => toggleAttrFilter(attrKey, value)}
            >
                <div className="flex items-center gap-2">
                    <Checkbox
                        color='black'
                        checked={isSelected}
                        onChange={() => { }}
                    />
                    {value}
                </div>
            </div>
        );
    };

    const filterTitleClassname = 'text-xs font-bold text-gray-900 cursor-pointer p-1 flex items-center justify-between';
    const filterSectionClassName = 'border-b border-gray-100 space-y-1 p-1';

    return (
        <div className="solid-box-border p-0">
            {/* Business Type Filter */}
            {filterOptions.categories.length > 0 && (
                <div className={filterSectionClassName}>
                    <div
                        className={filterTitleClassname}
                        onClick={() => toggleSection('category')}
                    >
                        <span>{t('type')}</span>
                    </div>
                    {expandedSections.has('category') && (
                        <div className="px-2 pb-2">
                            {filterOptions.categories.map(category =>
                                renderFilterButton('category', category, filters.category.includes(category))
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* City Filter */}
            {filterOptions.cities.length > 0 && (
                <div className={filterSectionClassName}>
                    <div
                        className={filterTitleClassname}
                        onClick={() => toggleSection('city')}
                    >
                        <span>{t('city')}</span>
                    </div>
                    {expandedSections.has('city') && (
                        <div className="px-2 pb-2">
                            {filterOptions.cities.map(city =>
                                renderFilterButton('city', city, filters.city.includes(city))
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Country Filter */}
            {filterOptions.countryCodes.length > 0 && (
                <div className={filterSectionClassName}>
                    <div
                        className={filterTitleClassname}
                        onClick={() => toggleSection('country_code')}
                    >
                        <span>{t('country')}</span>
                    </div>
                    {expandedSections.has('country_code') && (
                        <div className="px-2 pb-2">
                            {filterOptions.countryCodes.map(code =>
                                renderFilterButton('country_code', code, filters.country_code.includes(code))
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Discount Category Filter */}
            {filterOptions.discountCategories.length > 0 && (
                <div className={filterSectionClassName}>
                    <div
                        className={filterTitleClassname}
                        onClick={() => toggleSection('discount_category')}
                    >
                        <span>{t('discount_category')}</span>
                    </div>
                    {expandedSections.has('discount_category') && (
                        <div className="px-2 pb-2">
                            {filterOptions.discountCategories.map(discountCat =>
                                renderFilterButton('discount_category', discountCat, filters.discount_category.includes(discountCat))
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* My Companies Filter */}
            <div className={filterSectionClassName}>
                <div
                    className={filterTitleClassname}
                    onClick={() => toggleSection('my_companies')}
                >
                    <span>{t('my_companies')}</span>
                </div>
                {expandedSections.has('my_companies') && (
                    <div className="px-2 pb-2">
                        {renderFilterButton('my_companies', true, filters.my_companies.includes(true))}
                    </div>
                )}
            </div>

            {/* Attribute Filters */}
            {filters.category.length > 0 && Object.entries(attrValues).map(([attrKey, values]) => (
                <div key={attrKey} className={filterSectionClassName}>
                    <div
                        className={filterTitleClassname}
                        onClick={() => toggleSection(attrKey)}
                    >
                        <span>{t(attrKey)}</span>
                    </div>
                    {expandedSections.has(attrKey) && (
                        <div className="px-2 pb-2">
                            {values.map(value =>
                                renderAttrFilterButton(attrKey, value, (attrFilters[attrKey] || []).includes(value))
                            )}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}